import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SendIcon, BotIcon, UserIcon } from 'lucide-react';
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}
const SYSTEM_PROMPT = `Bạn là trợ lý AI của Mỹ Hòa Phát (MHP) — nhà cung cấp phụ tùng xe máy chính hãng hơn 15 năm. Trả lời ngắn gọn, thân thiện, bằng tiếng Việt.

CHƯƠNG TRÌNH KHÁCH HÀNG THÂN THIẾT:

VÍ TÍN DỤNG:
- Nạp tối thiểu 10.000.000đ (bội số 10 triệu)
- Nhận ngay +2.5% tín dụng thưởng mỗi lần nạp
- Tín dụng có hiệu lực 12 tháng
- Tín dụng chỉ dùng mua hàng tại MHP, không chuyển nhượng, không rút tiền mặt

ĐIỂM THƯỞNG:
- Mỗi 100.000đ chi tiêu = 1 điểm (nhân hệ số theo hạng)
- Quy đổi: 1.000 điểm = 500.000đ tín dụng mua hàng
- 1 điểm = 500đ giá trị tín dụng
- Kết hợp tín dụng thưởng 2.5% + điểm quy đổi ≈ tổng ~3% giá trị trả lại

HẠNG THÀNH VIÊN:
- Thành Viên: 0-999 điểm, tích điểm x1
- Bạc: 1.000-4.999 điểm, tích điểm x1.2, quà sinh nhật
- Vàng: 5.000-14.999 điểm, tích điểm x1.5, quà sinh nhật + ưu đãi riêng
- VIP: 15.000+ điểm, tích điểm x2, tất cả quyền lợi cao cấp

LIÊN HỆ:
- Zalo: 0903302489
- Shopee: https://shopee.vn/mhp138
- Website: myhoaphat.com

Nếu không biết câu trả lời, hướng dẫn khách liên hệ Zalo 0903302489.`;
const WELCOME_MESSAGE: Message = {
  id: 'welcome',
  role: 'assistant',
  content: 'Xin chào! 👋 Tôi là trợ lý AI của Mỹ Hòa Phát. Hỏi tôi bất cứ điều gì về chương trình khách hàng thân thiết — ví tín dụng, điểm thưởng, hạng thành viên, và nhiều hơn nữa!',
  timestamp: new Date()
};
function TypingIndicator() {
  return <div className="flex items-start gap-2.5 mb-3" data-id="element-95">
      <div className="w-7 h-7 rounded-full bg-[#C8A84E]/15 flex items-center justify-center flex-shrink-0" data-id="element-96">
        <BotIcon className="w-3.5 h-3.5 text-[#C8A84E]" data-id="element-97" />
      </div>
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl rounded-tl-sm px-4 py-3" data-id="element-98">
        <div className="flex items-center gap-1" data-id="element-99">
          <motion.div className="w-1.5 h-1.5 rounded-full bg-[#666666]" animate={{
          y: [0, -4, 0]
        }} transition={{
          duration: 0.6,
          repeat: Infinity,
          delay: 0
        }} data-id="element-100" />
          <motion.div className="w-1.5 h-1.5 rounded-full bg-[#666666]" animate={{
          y: [0, -4, 0]
        }} transition={{
          duration: 0.6,
          repeat: Infinity,
          delay: 0.15
        }} data-id="element-101" />
          <motion.div className="w-1.5 h-1.5 rounded-full bg-[#666666]" animate={{
          y: [0, -4, 0]
        }} transition={{
          duration: 0.6,
          repeat: Infinity,
          delay: 0.3
        }} data-id="element-102" />
        </div>
      </div>
    </div>;
}
interface AIChatbotProps {
  isOpen: boolean;
  onClose: () => void;
}
export function AIChatbot({
  isOpen,
  onClose
}: AIChatbotProps) {
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, []);
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);
  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    try {
      const chatHistory = [...messages.filter(m => m.id !== 'welcome'), userMessage].map(m => ({
        role: m.role,
        content: m.content
      }));
      const response = await fetch('https://www.requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [{
            role: 'system',
            content: SYSTEM_PROMPT
          }, ...chatHistory],
          temperature: 0.7,
          max_tokens: 500
        })
      });
      if (!response.ok) {
        throw new Error('API request failed');
      }
      const data = await response.json();
      const assistantContent = data.choices?.[0]?.message?.content || 'Xin lỗi, tôi không thể trả lời lúc này. Vui lòng liên hệ Zalo 0903302489.';
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch {
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Xin lỗi, đã có lỗi xảy ra. Vui lòng thử lại hoặc liên hệ Zalo 0903302489 để được hỗ trợ trực tiếp.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  return <AnimatePresence data-id="element-103">
      {isOpen && <>
          {/* Backdrop on mobile */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/40 z-[60] md:hidden" onClick={onClose} data-id="element-104" />

          {/* Chat panel */}
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.95
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 28
      }} className="fixed z-[61] bottom-0 right-0 md:bottom-24 md:right-6 w-full md:w-[400px] md:max-w-[calc(100vw-48px)] md:rounded-2xl overflow-hidden bg-[#0D0D0D] border border-[#2A2A2A] shadow-2xl shadow-black/40 flex flex-col" style={{
        maxHeight: 'min(85vh, 640px)'
      }} data-id="element-105">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A2A] bg-[#0D0D0D] flex-shrink-0" data-id="element-106">
              <div className="flex items-center gap-2.5" data-id="element-107">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C8A84E] to-[#B8963E] flex items-center justify-center" data-id="element-108">
                  <BotIcon className="w-4 h-4 text-[#0D0D0D]" data-id="element-109" />
                </div>
                <div data-id="element-110">
                  <h3 className="text-sm font-semibold text-white leading-tight" data-id="element-111">
                    Trợ Lý MHP
                  </h3>
                  <div className="flex items-center gap-1.5" data-id="element-112">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" data-id="element-113" />
                    <span className="text-[10px] text-[#22C55E]" data-id="element-114">Online</span>
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-[#666666] hover:text-white hover:bg-[#2A2A2A] transition-colors" aria-label="Đóng chat" data-id="element-115">
                <XIcon className="w-4 h-4" data-id="element-116" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1 min-h-0" data-id="element-117">
              {messages.map(msg => <motion.div key={msg.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.2
          }} className={`flex mb-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-118">
                  {msg.role === 'assistant' && <div className="w-7 h-7 rounded-full bg-[#C8A84E]/15 flex items-center justify-center flex-shrink-0 mr-2.5 mt-0.5" data-id="element-119">
                      <BotIcon className="w-3.5 h-3.5 text-[#C8A84E]" data-id="element-120" />
                    </div>}
                  <div className={`max-w-[80%] px-3.5 py-2.5 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-[#C8A84E] text-[#0D0D0D] rounded-2xl rounded-tr-sm' : 'bg-[#1A1A1A] border border-[#2A2A2A] text-[#E0E0E0] rounded-2xl rounded-tl-sm'}`} data-id="element-121">
                    {msg.content.split('\n').map((line, i) => <Fragment key={i} data-id="element-122">
                        {line}
                        {i < msg.content.split('\n').length - 1 && <br data-id="element-123" />}
                      </Fragment>)}
                  </div>
                  {msg.role === 'user' && <div className="w-7 h-7 rounded-full bg-[#2A2A2A] flex items-center justify-center flex-shrink-0 ml-2.5 mt-0.5" data-id="element-124">
                      <UserIcon className="w-3.5 h-3.5 text-[#B0B0B0]" data-id="element-125" />
                    </div>}
                </motion.div>)}
              {isLoading && <TypingIndicator data-id="element-126" />}
              <div ref={messagesEndRef} data-id="element-127" />
            </div>

            {/* Quick suggestions */}
            {messages.length <= 1 && <div className="px-4 pb-2 flex flex-wrap gap-1.5" data-id="element-128">
                {['Cách nạp tiền vào ví?', 'Điểm thưởng tính sao?', 'Hạng VIP có gì?'].map(q => <button key={q} onClick={() => {
            setInput(q);
            setTimeout(() => {
              setInput(q);
              sendMessage();
            }, 50);
          }} className="text-xs px-3 py-1.5 rounded-full border border-[#2A2A2A] text-[#B0B0B0] hover:text-[#C8A84E] hover:border-[#C8A84E]/30 transition-colors" data-id="element-129">
                    {q}
                  </button>)}
              </div>}

            {/* Input */}
            <div className="border-t border-[#2A2A2A] px-3 py-3 bg-[#0D0D0D] flex-shrink-0" data-id="element-130">
              <div className="flex items-center gap-2" data-id="element-131">
                <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Nhập câu hỏi..." className="flex-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#666666] focus:outline-none focus:border-[#C8A84E]/50 transition-colors" disabled={isLoading} data-id="element-132" />
                <button onClick={sendMessage} disabled={!input.trim() || isLoading} className="w-10 h-10 rounded-xl bg-[#C8A84E] flex items-center justify-center text-[#0D0D0D] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#B8963E] transition-colors flex-shrink-0" aria-label="Gửi" data-id="element-133">
                  <SendIcon className="w-4 h-4" data-id="element-134" />
                </button>
              </div>
              <p className="text-[9px] text-[#666666] text-center mt-1.5" data-id="element-135">
                AI có thể sai. Liên hệ Zalo 0903302489 để xác nhận.
              </p>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}